/**
 * Reads a cookie value from a cookie string by name.
 * Handles all positions: first, middle, and last cookie in the string.
 *
 * @returns The cookie value, or null if the cookie is not present.
 */
export function readCookieValue(cookieString: string, name: string): string | null {
  const escapedName = name.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const match = cookieString.match(new RegExp(String.raw`(?:^|;\s*)${escapedName}=([^;]*)`));
  return match?.[1] ?? null;
}

interface SetCookieOptions {
  path?: string;
  maxAge?: number;
  sameSite?: 'Strict' | 'Lax' | 'None';
  secure?: boolean;
}

/**
 * Minimal shape of the browser globals these client-cookie helpers touch.
 * Typed locally so the module compiles in server-only packages whose tsconfig
 * omits the `dom` lib (FPO-2354); these functions still only run in a browser.
 */
interface CookieDomGlobals {
  location?: { protocol: string };
  document: { cookie: string };
}

/**
 * Sets a client-side cookie with typed options.
 * Automatically adds the `Secure` flag on HTTPS origins.
 */
export function setClientCookie(name: string, value: string, options: SetCookieOptions = {}): void {
  const parts = [`${name}=${value}`];
  if (options.path != null) parts.push(`path=${options.path}`);
  if (options.maxAge != null) parts.push(`max-age=${options.maxAge}`);
  if (options.sameSite != null) parts.push(`SameSite=${options.sameSite}`);
  const { location, document } = globalThis as unknown as CookieDomGlobals;
  const secure = options.secure ?? location?.protocol === 'https:';
  if (secure) parts.push('Secure');
  // `document.cookie` rather than the Cookie Store API, which lacks Safari/Firefox support.
  document.cookie = parts.join('; ');
}

/** Clears a client-side cookie by setting it to empty with max-age=0. */
export function clearClientCookie(name: string, path = '/'): void {
  setClientCookie(name, '', { path, maxAge: 0 });
}
